<!--
  Child Table Component for Receiving Log
  ------------------------------------------------------
  - Displays a paginated, sortable table of receiving logs.
  - On phone-sized screens (isSmallScreen) it swaps the wide scrolling table for
    a stacked card list with large tap targets.
  - Damaged entries are flagged prominently on both layouts.
  - Supports filtering, sorting, and navigation to entry details.
  - Shows a loading indicator while data is being fetched.
  - Includes pagination controls for navigating multiple pages of logs.
-->
<div class="full-table-wrapper">
  <!-- Display message if no data is available and loading is complete -->
  <ion-items
    *ngIf="!dataLoading.isLoading && items.length === 0"
    class="centered-item"
  >
    <ion-note color="danger">{{ tableConfig.notes.noLogs }}</ion-note>
  </ion-items>

  <div class="outer-wrapper">
    <div class="table-wrapper">
      <div class="title-wrapper">
        <!-- Table Header with Title and Action Buttons.
             Title is desktop-only — on mobile it duplicates the header and eats
             vertical space, so the card list leads straight with the actions. -->
        <h3 class="table-title" *ngIf="!isSmallScreen">{{ tableConfig.title }}</h3>
        <div class="title-actions">
          <!-- Sort button — mobile only (desktop sorts via the column headers).
               Opens an action sheet of the number/date columns to sort by. -->
          <ion-button
            *ngIf="isSmallScreen && sortableColumns.length > 0"
            (click)="openSortSheet()"
            class="sort-button"
            size="small"
            color
          >
            <ion-icon slot="start" name="swap-vertical"></ion-icon>
            Sort
          </ion-button>

          <!-- Button to open the filter modal -->
          <ion-button
            (click)="handleOpenFilterModal()"
            class="filter-button"
            size="small"
            color
          >
            <ion-icon slot="start" name="funnel"></ion-icon>
            {{ tableConfig.buttons.filterButton }}
          </ion-button>

          <!-- Button to navigate to the form for adding a new entry -->
          <ion-button
            (click)="handleNavToPage()"
            class="button-color-logo"
            size="small"
          >
            <ion-icon slot="start" name="create"></ion-icon>
            {{ tableConfig.buttons.newEntryButton }}
          </ion-button>
        </div>
      </div>

      <!-- Table Content Wrapper -->
      <div class="scrollable-table-wrapper">
        <!-- Show a loading spinner when data is being fetched -->
        <ng-container *ngIf="dataLoading.isLoading">
          <ion-item>
            <div class="spinner-container">
              <ion-spinner name="dots"></ion-spinner> {{ dataLoading.message }}
            </div>
          </ion-item>
        </ng-container>

        <!-- ===================== DESKTOP / TABLET: scrolling table ===================== -->
        <div
          class="scrollable-table"
          *ngIf="!dataLoading.isLoading && !isSmallScreen"
        >
          <table>
            <thead>
              <tr>
                <!-- Narrow leading column for the damaged-items flag -->
                <th class="flag-col" aria-label="Damaged"></th>
                <!-- Generate table headers dynamically based on column data -->
                <th *ngFor="let column of columns">
                  <div
                    class="sorting-container"
                    (click)="handleSortByColumn(column.name)"
                  >
                    <div class="column-header">{{ column.label }}</div>
                    <!-- Sorting icon to indicate clickable sorting functionality -->
                    <ion-icon slot="end" name="swap-vertical"></ion-icon>
                  </div>
                </th>
                <!----actions column header-->
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <!-- Generate table rows dynamically from items list -->
              <tr
                *ngFor="let item of items; let i = index"
                [ngClass]="{ 'even-row': i % 2 === 0, 'damaged-row': rowHasDamage(item) }"
                (click)="handleOpenRowModule(item)"
              >
                <!-- Damaged-items flag: red warning marker, empty otherwise -->
                <td class="flag-col">
                  <ion-icon
                    *ngIf="rowHasDamage(item)"
                    class="damage-flag-icon"
                    name="warning"
                    title="This entry has damaged items"
                    aria-label="Damaged items"
                  ></ion-icon>
                </td>

                <!-- Display each item's data in the respective columns -->
                <td *ngFor="let column of columns">{{ item[column.name] }}</td>

                <!-- Row Actions Column (includes generate email and delete log entry button) -->
                    <td >
                      <ion-buttons>

                      <!-- for all users, show the button for generating an email out of the log entry-->
                      <ion-button
                        *ngIf="tableConfig.hasEmailFeature"
                        color="primary"
                        fill="clear"
                        title="{{ tableConfig.buttons.generateEmailButton }}"
                        (click)="handleGenerateEmail(item); $event.stopPropagation()"
                      >
                        <ion-icon slot="icon-only" name="mail"></ion-icon>
                      </ion-button>

                      <!-- for all users, show the button for reprinting the log entry's labels -->
                      <ion-button
                        *ngIf="tableConfig.hasReprintFeature"
                        color="primary"
                        fill="clear"
                        title="{{ tableConfig.buttons.reprintLabelsButton }}"
                        (click)="handleReprintRow(item); $event.stopPropagation()"
                      >
                        <ion-icon slot="icon-only" name="print"></ion-icon>
                      </ion-button>

                       <!-- If user is an admin, show the Column with a delete button for deleting a log entry row (normal table) -->
                      <ion-button
                      *ngIf="user.isAdmin && !isArchiveTable"
                      color="danger"
                      fill="clear"
                      title="{{ tableConfig.buttons.deleteEntryButton }}"
                      (click)="handleDeleteRow(item.id); $event.stopPropagation()"
                    >
                      <ion-icon slot="icon-only" name="trash"></ion-icon>
                    </ion-button>

                    <!-- If user is an admin and this is an archive table, show the restore button -->
                      <ion-button
                      *ngIf="user.isAdmin && isArchiveTable"
                      color="success"
                      fill="clear"
                      title="Restore Entry from Archive"
                      (click)="handleRestoreRow(item.id); $event.stopPropagation()"
                    >
                      <ion-icon slot="icon-only" name="refresh"></ion-icon>
                    </ion-button>
                    </ion-buttons>
                    </td>


              </tr>
            </tbody>
          </table>
        </div>

        <!-- ===================== MOBILE: stacked card list ===================== -->
        <div
          class="card-list"
          *ngIf="!dataLoading.isLoading && isSmallScreen"
        >
          <div
            class="log-card"
            *ngFor="let item of items"
            [class.damaged-card]="rowHasDamage(item)"
            (click)="handleOpenRowModule(item)"
          >
            <!-- Card header: primary identifier (left) + secondary identifier and
                 the ⋮ actions menu (right). Tapping the card opens details, so the
                 menu button stops propagation. -->
            <div class="log-card-header">
              <div class="log-card-title">
                <span class="log-card-title-label" *ngIf="cardTitleColumn">{{ cardTitleColumn.label }}</span>
                <span class="log-card-title-value">{{ (cardTitleColumn && item[cardTitleColumn.name]) || '—' }}</span>
              </div>
              <div class="log-card-header-right">
                <div class="log-card-title log-card-title-right" *ngIf="cardTitleRightColumn">
                  <span class="log-card-title-label">{{ cardTitleRightColumn.label }}</span>
                  <span class="log-card-title-value">{{ item[cardTitleRightColumn.name] || '—' }}</span>
                </div>
                <ion-button
                  class="log-card-menu-btn"
                  fill="clear"
                  aria-label="Log actions"
                  title="Log actions"
                  (click)="openCardActions(item); $event.stopPropagation()"
                >
                  <ion-icon slot="icon-only" name="ellipsis-vertical"></ion-icon>
                </ion-button>
              </div>
            </div>

            <!-- Damaged flag on its own row so it stands out -->
            <div class="damage-badge-row" *ngIf="rowHasDamage(item)">
              <span class="damage-badge">
                <ion-icon name="warning"></ion-icon>
                Damaged
              </span>
            </div>

            <!-- Card body: a short, curated set of fields (skip blanks) -->
            <div class="log-card-body">
              <ng-container *ngFor="let column of cardColumns">
                <div
                  class="log-card-field"
                  *ngIf="item[column.name] !== undefined && item[column.name] !== null && item[column.name] !== ''"
                >
                  <span class="field-label">{{ column.label }}</span>
                  <span class="field-value">{{ item[column.name] }}</span>
                </div>
              </ng-container>
            </div>

          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div class="pagination-controls" *ngIf="!dataLoading.isLoading && items.length > 0">
        <!-- Button to go to the previous page -->
        <ion-button
          size="small"
          fill="outline"
          (click)="prevPage()"
          [disabled]="currentPage === 1"
        >
          <ion-icon slot="start" name="chevron-back-outline"></ion-icon>
          Prev
        </ion-button>
        <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
        <!-- Button to go to the next page -->
        <ion-button
          size="small"
          fill="outline"
          (click)="nextPage()"
          [disabled]="currentPage === totalPages"
        >
          Next
          <ion-icon slot="end" name="chevron-forward-outline"></ion-icon>
        </ion-button>
      </div>
    </div>
  </div>
</div>
